// The Auth0 SDK's state and methods, readable without the SDK. The SDK is loaded lazily, and
// only when Auth0 is configured, so nothing outside Auth0Provider/Auth0SdkHost may import it
// at runtime. The host publishes here; React reads through useAuth0Session, and code outside
// the tree -- the axios interceptors, the sign-out -- through the functions below.
import type {
    CustomTokenExchangeOptions,
    GetTokenSilentlyOptions,
    LogoutOptions,
    RedirectLoginOptions,
    TokenEndpointResponse,
} from '@auth0/auth0-react';

import { isAuth0Configured } from './config';

export interface Auth0SessionState {
    isLoading: boolean;
    isAuthenticated: boolean;
    error?: Error;
    // The SDK chunk failed to load, so nothing will ever be published or registered.
    loadFailed: boolean;
}

export interface Auth0Api {
    getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
    loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
    logout: (options?: LogoutOptions) => Promise<void>;
    loginWithCustomTokenExchange: (options: CustomTokenExchangeOptions) => Promise<TokenEndpointResponse>;
}

interface ApiWaiter {
    resolve: (api: Auth0Api) => void;
    reject: (error: Error) => void;
}

// Loading from the start when configured: until the host has mounted and the SDK has
// checked for a session, "not authenticated" would be a guess.
function initialState(): Auth0SessionState {
    return { isLoading: isAuth0Configured(), isAuthenticated: false, loadFailed: false };
}

let state = initialState();
let api: Auth0Api | null = null;
let apiWaiters: ApiWaiter[] = [];
let settledWaiters: Array<() => void> = [];
const listeners = new Set<() => void>();

export function getAuth0State(): Auth0SessionState {
    return state;
}

export function subscribeAuth0State(listener: () => void): () => void {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

export function publishAuth0State(partial: Partial<Auth0SessionState>): void {
    const next = { ...state, ...partial };
    const keys = Object.keys(next) as Array<keyof Auth0SessionState>;

    // A new object only when something changed: useSyncExternalStore re-renders on identity.
    if (keys.every((key) => next[key] === state[key])) {
        return;
    }
    state = next;

    if (!state.isLoading) {
        const settled = settledWaiters;
        settledWaiters = [];
        settled.forEach((resolve) => resolve());
    }

    listeners.forEach((listener) => listener());
}

export function registerAuth0Api(next: Auth0Api | null): void {
    api = next;
    if (!next) {
        return;
    }

    const waiting = apiWaiters;
    apiWaiters = [];
    waiting.forEach(({ resolve }) => resolve(next));
}

/**
 * The SDK's methods, once the host has registered them. Rejects when they will never
 * arrive: Auth0 is not configured, or the SDK failed to load.
 */
export function whenAuth0Api(): Promise<Auth0Api> {
    if (api) {
        return Promise.resolve(api);
    }
    if (!isAuth0Configured()) {
        return Promise.reject(new Error('Auth0 is not configured'));
    }
    if (state.loadFailed) {
        return Promise.reject(state.error ?? new Error('The Auth0 SDK failed to load'));
    }

    return new Promise((resolve, reject) => {
        apiWaiters.push({ resolve, reject });
    });
}

/** Resolves once the SDK has decided whether a session exists. */
export function whenAuth0Settled(): Promise<void> {
    if (!state.isLoading) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        settledWaiters.push(resolve);
    });
}

export function failAuth0Load(error: Error): void {
    publishAuth0State({ isLoading: false, isAuthenticated: false, error, loadFailed: true });

    const waiting = apiWaiters;
    apiWaiters = [];
    waiting.forEach(({ reject }) => reject(error));
}

export function resetAuth0StateForTests(): void {
    state = initialState();
    api = null;
    apiWaiters = [];
    settledWaiters = [];
    listeners.forEach((listener) => listener());
}
